import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import {
  Search,
  X,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Clock,
  Boxes,
  EyeOff,
  AlertTriangle,
  Building2,
  Barcode,
  ShoppingBag,
  ArrowRight,
  Copy,
  Check,
  Store,
  RefreshCw,
  Tag,
  PackageX,
} from 'lucide-react';
import {
  searchProductLifecycle,
  hideProductOnWooCommerce,
  parkHiddenStock,
  syncProductFromWoo,
  type ProductLifecycleItem,
} from '../api.js';
import { Button } from '../ui/index.js';
import { cn } from '../ui/cn.js';

interface ProductLifecycleModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialQuery?: string;
}

export function ProductLifecycleModal({ isOpen, onClose, initialQuery = '' }: ProductLifecycleModalProps) {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState(initialQuery);
  const [activeQuery, setActiveQuery] = useState(initialQuery);
  const [copiedSku, setCopiedSku] = useState<string | null>(null);
  const [hidingId, setHidingId] = useState<number | null>(null);
  const [feedbackMsg, setFeedbackMsg] = useState<{ text: string; tone: 'ok' | 'error' } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setSearchTerm(initialQuery);
      setActiveQuery(initialQuery);
      setFeedbackMsg(null);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen, initialQuery]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['lifecycle-search', activeQuery],
    queryFn: () => searchProductLifecycle(activeQuery),
    enabled: isOpen && activeQuery.trim().length >= 2,
    staleTime: 10_000,
  });

  const [parkingStockId, setParkingStockId] = useState<number | null>(null);

  const parkStockMutation = useMutation({
    mutationFn: (productId: number) => parkHiddenStock([productId]),
    onSuccess: (_data, productId) => {
      setFeedbackMsg({
        text: `Stock du produit #${productId} parqué avec succès à -999 sur WooCommerce (exclu des flux Facebook Ads et Google Shopping).`,
        tone: 'ok',
      });
      void queryClient.invalidateQueries({ queryKey: ['lifecycle-search'] });
      void queryClient.invalidateQueries({ queryKey: ['hidden-products'] });
    },
    onError: (err) => {
      setFeedbackMsg({
        text: `Erreur lors du parking du stock : ${err instanceof Error ? err.message : String(err)}`,
        tone: 'error',
      });
    },
    onSettled: () => {
      setParkingStockId(null);
    },
  });

  const handleParkStock = (product: ProductLifecycleItem) => {
    setParkingStockId(product.id);
    parkStockMutation.mutate(product.id);
  };

  const hideMutation = useMutation({
    mutationFn: async (productId: number) => {
      const hideRes = await hideProductOnWooCommerce(productId);
      // Automatically park stock at -999 on WooCommerce so the product immediately leaves ads feeds
      await parkHiddenStock([productId]).catch(() => undefined);
      return hideRes;
    },
    onSuccess: (_data, productId) => {
      setFeedbackMsg({
        text: `Produit #${productId} masqué avec succès du catalogue et stock parqué à -999 (retiré des flux Google/Facebook Ads).`,
        tone: 'ok',
      });
      void queryClient.invalidateQueries({ queryKey: ['lifecycle-search'] });
      void queryClient.invalidateQueries({ queryKey: ['hidden-products'] });
      void queryClient.invalidateQueries({ queryKey: ['b2b-reconciliation'] });
      void queryClient.invalidateQueries({ queryKey: ['final-ean-match'] });
      void queryClient.invalidateQueries({ queryKey: ['final-ean-match-kpis'] });
      void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['kpis'] });
    },
    onError: (err) => {
      setFeedbackMsg({
        text: `Erreur lors du masquage : ${err instanceof Error ? err.message : String(err)}`,
        tone: 'error',
      });
    },
    onSettled: () => {
      setHidingId(null);
    },
  });

  const handleHideProduct = (product: ProductLifecycleItem) => {
    if (
      window.confirm(
        `Masquer le produit #${product.id} "${product.name}" ?\n\n` +
          `• Statut opérateur : marqué "Masqué" / Discontinué.\n` +
          `• WooCommerce : retiré du site web (catalog_visibility = hidden).\n` +
          `• Stock WooCommerce : parqué à -999 (exclu de Facebook Ads & Google Shopping).\n` +
          `• Rapprochement : exclu des files de révision et des prix B2B.`,
      )
    ) {
      setHidingId(product.id);
      hideMutation.mutate(product.id);
    }
  };

  const [syncingId, setSyncingId] = useState<number | null>(null);

  const syncMutation = useMutation({
    mutationFn: (productId: number) => syncProductFromWoo(productId),
    onSuccess: (_data, productId) => {
      setFeedbackMsg({
        text: `Produit #${productId} synchronisé avec succès depuis WooCommerce.`,
        tone: 'ok',
      });
      void queryClient.invalidateQueries({ queryKey: ['lifecycle-search'] });
      void queryClient.invalidateQueries({ queryKey: ['b2b-reconciliation'] });
      void queryClient.invalidateQueries({ queryKey: ['final-ean-match'] });
    },
    onError: (err) => {
      setFeedbackMsg({
        text: `Erreur lors de la synchronisation Woo : ${err instanceof Error ? err.message : String(err)}`,
        tone: 'error',
      });
    },
    onSettled: () => {
      setSyncingId(null);
    },
  });

  const handleSyncProduct = (product: ProductLifecycleItem) => {
    setSyncingId(product.id);
    syncMutation.mutate(product.id);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim().length >= 2) {
      setActiveQuery(searchTerm.trim());
    }
  };

  const handleCopySku = (sku: string) => {
    navigator.clipboard.writeText(sku);
    setCopiedSku(sku);
    setTimeout(() => setCopiedSku(null), 2000);
  };

  const navigateTo = (path: string) => {
    onClose();
    navigate(path);
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-black/60 backdrop-blur-xs overflow-y-auto animate-in fade-in duration-150"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-5xl max-h-[92vh] flex flex-col bg-card rounded-2xl border border-border shadow-2xl overflow-hidden my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-cream/40">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-herb-100 text-herb-800 border border-herb-200">
              <Search className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-ink">Recherche & Diagnostic Cycle de Vie Produit</h2>
              <p className="text-xs text-ink-muted">
                Statut instantané WooCommerce, Couche 1 (Workbench), Couche 2 (Final EAN Match) et Prix B2B Sage
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-ink-muted hover:text-ink hover:bg-cream transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search Input Bar */}
        <div className="p-4 sm:px-6 border-b border-border bg-card">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-ghost" />
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Entrez un SKU (ex: K2M17), Code-barres EAN (ex: 333787...), ou Nom..."
                className="w-full pl-10 pr-4 py-2.5 text-sm bg-cream/30 border border-border rounded-xl focus:outline-hidden focus:ring-2 focus:ring-herb-500/20 focus:border-herb-600 transition-all font-mono"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm('');
                    setActiveQuery('');
                    inputRef.current?.focus();
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-ghost hover:text-ink"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button type="submit" variant="primary" disabled={searchTerm.trim().length < 2 || isFetching}>
              {isFetching ? 'Recherche...' : 'Rechercher'}
            </Button>
          </form>

          {/* Quick example tags */}
          <div className="flex items-center gap-2 mt-2.5 text-xs text-ink-faint">
            <span>Exemples rapides :</span>
            {['K2M17', 'DERMEDIC', 'LA ROCHE POSAY', 'TOPFACE'].map((sample) => (
              <button
                key={sample}
                type="button"
                onClick={() => {
                  setSearchTerm(sample);
                  setActiveQuery(sample);
                }}
                className="px-2 py-0.5 rounded-md bg-cream/80 hover:bg-cream border border-border font-mono text-[11px] text-ink-soft hover:text-ink transition-colors"
              >
                {sample}
              </button>
            ))}
          </div>
        </div>

        {/* Results Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
          {feedbackMsg && (
            <div
              className={cn(
                'p-3 rounded-xl text-xs font-semibold flex items-center justify-between border shadow-2xs',
                feedbackMsg.tone === 'ok'
                  ? 'bg-emerald-50 text-emerald-900 border-emerald-300'
                  : 'bg-rose-50 text-rose-900 border-rose-300',
              )}
            >
              <span>{feedbackMsg.text}</span>
              <button
                type="button"
                onClick={() => setFeedbackMsg(null)}
                className="p-1 text-ink-muted hover:text-ink cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {isLoading ? (
            <div className="py-16 text-center space-y-3">
              <div className="w-8 h-8 mx-auto border-3 border-herb-600 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm font-medium text-ink-soft">Analyse du cycle de vie en cours...</p>
            </div>
          ) : activeQuery.trim().length < 2 ? (
            <div className="py-16 text-center text-ink-muted">
              <Search className="w-10 h-10 mx-auto text-ink-ghost mb-2" />
              <p className="text-sm">Saisissez au moins 2 caractères pour inspecter le statut d'un produit.</p>
            </div>
          ) : data?.items.length === 0 ? (
            <div className="py-16 text-center space-y-2">
              <AlertTriangle className="w-10 h-10 mx-auto text-amber-500 mb-2" />
              <p className="text-sm font-semibold text-ink">Aucun produit trouvé pour « {activeQuery} »</p>
              <p className="text-xs text-ink-muted max-w-md mx-auto">
                Vérifiez l'orthographe du SKU ou du code-barres. Si le produit vient d'être créé sur WooCommerce, il
                sera synchronisé lors du prochain miroir.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-xs text-ink-muted font-medium px-1">
                <span>
                  {(data?.items.length ?? 0)} produit{(data?.items.length ?? 0) > 1 ? 's' : ''} trouvé{(data?.items.length ?? 0) > 1 ? 's' : ''}
                </span>
                <span>Cliquez sur une action pour naviguer directement</span>
              </div>

              {data?.items.map((product) => (
                <ProductLifecycleCard
                  key={product.id}
                  product={product}
                  copiedSku={copiedSku}
                  onCopySku={handleCopySku}
                  onNavigate={navigateTo}
                  onHide={handleHideProduct}
                  isHiding={hidingId === product.id}
                  onParkStock={handleParkStock}
                  isParkingStock={parkingStockId === product.id}
                  onSyncFromWoo={handleSyncProduct}
                  isSyncing={syncingId === product.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ProductLifecycleCard({
  product,
  copiedSku,
  onCopySku,
  onNavigate,
  onHide,
  isHiding,
  onParkStock,
  isParkingStock,
  onSyncFromWoo,
  isSyncing,
}: {
  product: ProductLifecycleItem;
  copiedSku: string | null;
  onCopySku: (sku: string) => void;
  onNavigate: (path: string) => void;
  onHide: (product: ProductLifecycleItem) => void;
  isHiding: boolean;
  onParkStock: (product: ProductLifecycleItem) => void;
  isParkingStock: boolean;
  onSyncFromWoo: (product: ProductLifecycleItem) => void;
  isSyncing: boolean;
}) {
  const formatTnd = (val: number | null) => {
    if (val === null || val === undefined) return 'N/A';
    return (val / 1000).toFixed(3) + ' TND';
  };

  return (
    <div className="bg-card rounded-xl border border-border hover:border-herb-500/40 shadow-xs hover:shadow-md transition-all overflow-hidden">
      {/* Product Header Banner */}
      <div className="p-4 bg-cream/30 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          {product.images[0] ? (
            <img
              src={product.images[0]}
              alt={product.name}
              className="w-12 h-12 object-contain rounded-lg border border-border bg-white shrink-0 p-0.5"
            />
          ) : (
            <div className="w-12 h-12 rounded-lg border border-border bg-cream grid place-items-center shrink-0">
              <ShoppingBag className="w-6 h-6 text-ink-ghost" />
            </div>
          )}
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-ink leading-snug break-words">{product.name}</h3>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {product.brand && (
                <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-sky-100 text-sky-800 border border-sky-200">
                  {product.brand}
                </span>
              )}
              <button
                type="button"
                onClick={() => onCopySku(product.sku)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-mono text-xs font-semibold bg-white border border-border text-ink hover:bg-cream transition-colors"
                title="Cliquer pour copier le SKU"
              >
                <span>SKU: {product.sku}</span>
                {copiedSku === product.sku ? (
                  <Check className="w-3 h-3 text-emerald-600" />
                ) : (
                  <Copy className="w-3 h-3 text-ink-ghost" />
                )}
              </button>
              <span className="text-[11px] font-mono text-ink-ghost">Woo #{product.wcProductId}</span>
              {product.wcType && (
                <span className="text-[11px] text-ink-faint capitalize">({product.wcType})</span>
              )}
            </div>
          </div>
        </div>

        {/* Global Summary Badge */}
        <div className="shrink-0 flex items-center gap-2 self-start sm:self-center">
          {product.operatorStatus === 'hide' || product.couche1.code === 'hide' ? (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-zinc-200 text-zinc-800 border border-zinc-300 flex items-center gap-1.5 shadow-2xs">
              <EyeOff className="w-4 h-4 text-zinc-600" />
              Masqué du site
            </span>
          ) : product.couche2.isConfirmed ? (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              100% Confirmé (Couche 2)
            </span>
          ) : product.couche1.code === 'approved' ? (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800 border border-blue-300 flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-blue-600" />
              En attente Couche 2 (Final Match)
            </span>
          ) : product.couche1.code === 'rejected' ? (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-rose-100 text-rose-800 border border-rose-300 flex items-center gap-1.5">
              <XCircle className="w-4 h-4 text-rose-600" />
              Rejeté Couche 1
            </span>
          ) : product.couche1.code === 'pack' ? (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-800 border border-purple-300 flex items-center gap-1.5">
              <Boxes className="w-4 h-4 text-purple-600" />
              Pack (Écarté)
            </span>
          ) : (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-300 flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-amber-600" />
              {product.couche1.label}
            </span>
          )}
        </div>
      </div>

      {/* 3 Pillars Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border">
        {/* Pillar 1: WooCommerce & Site */}
        <div className="p-4 space-y-2.5">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink-muted">
            <Store className="w-3.5 h-3.5 text-sky-600" />
            <span>1. WooCommerce & Site</span>
          </div>

          <div className="space-y-1.5 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-ink-muted">Statut Woo :</span>
              <div className="flex items-center gap-1.5">
                {product.operatorStatus === 'hide' || product.couche1.code === 'hide' || !product.isVisible ? (
                  <span className="px-2 py-0.5 rounded-md font-semibold bg-zinc-200 text-zinc-800 border border-zinc-300 flex items-center gap-1">
                    <EyeOff className="w-3 h-3 text-zinc-600" />
                    Masqué
                  </span>
                ) : product.status === 'publish' && product.isPublished ? (
                  <span className="px-2 py-0.5 rounded-md font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    Publié en ligne
                  </span>
                ) : product.status === 'trash' ? (
                  <span className="px-2 py-0.5 rounded-md font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                    Corbeille Woo
                  </span>
                ) : product.status === 'draft' ? (
                  <span className="px-2 py-0.5 rounded-md font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                    Brouillon
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded-md font-semibold bg-zinc-100 text-zinc-700 border border-zinc-200">
                    Hors ligne
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => onSyncFromWoo(product)}
                  disabled={isSyncing}
                  className="p-1 rounded text-ink-muted hover:text-sky-700 hover:bg-sky-50 transition-colors cursor-pointer"
                  title="Actualiser depuis WooCommerce"
                >
                  <RefreshCw className={cn("w-3 h-3", isSyncing && "animate-spin text-sky-600")} />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-ink-muted">Stock :</span>
              {product.inStock ? (
                <span className="font-semibold text-emerald-700">
                  En stock ({product.stockQty ?? 0})
                </span>
              ) : (
                <span className="font-semibold text-rose-600">
                  Rupture ({product.stockQty ?? 0})
                </span>
              )}
            </div>

            <div className="flex items-center justify-between">
              <span className="text-ink-muted">Prix Vente TTC :</span>
              <span className="font-mono font-bold text-ink">
                {formatTnd(product.salePriceTtc ?? product.regularPriceTtc)}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-ink-muted">EAN Woo :</span>
              <span className="font-mono font-medium text-ink">
                {product.barcodeRaw || product.eanRaw || (
                  <span className="text-ink-faint italic font-sans">Vide sur Woo</span>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Pillar 2: Parcours GTIN (Couche 1 & Couche 2) */}
        <div className="p-4 space-y-2.5">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink-muted">
            <Barcode className="w-3.5 h-3.5 text-herb-600" />
            <span>2. Parcours GTIN & Matching</span>
          </div>

          <div className="space-y-2 text-xs">
            {/* Couche 1 */}
            <div className="p-2 rounded-lg bg-cream/40 border border-border/80 space-y-1">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-ink">Couche 1 (Workbench) :</span>
                <span
                  className={cn(
                    'font-bold px-1.5 py-0.2 rounded text-[11px]',
                    product.couche1.code === 'approved'
                      ? 'bg-emerald-100 text-emerald-800'
                      : product.couche1.code === 'rejected'
                        ? 'bg-rose-100 text-rose-800'
                        : 'bg-amber-100 text-amber-800',
                  )}
                >
                  {product.couche1.label}
                </span>
              </div>
              {product.couche1.approvedGtin && (
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-ink-muted">EAN approuvé :</span>
                  <span className="font-mono font-bold text-ink">{product.couche1.approvedGtin}</span>
                </div>
              )}
              {product.couche1.decidedBy && (
                <div className="text-[10px] text-ink-faint">
                  Par {product.couche1.decidedBy} le {new Date(product.couche1.decidedAt!).toLocaleDateString('fr-FR')}
                </div>
              )}
            </div>

            {/* Couche 2 */}
            <div className="p-2 rounded-lg bg-cream/40 border border-border/80 space-y-1">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-ink">Couche 2 (Final Match) :</span>
                <span
                  className={cn(
                    'font-bold px-1.5 py-0.2 rounded text-[11px]',
                    product.couche2.isConfirmed
                      ? 'bg-emerald-100 text-emerald-800'
                      : product.couche2.code === 'pending_layer2'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-zinc-100 text-zinc-600',
                  )}
                >
                  {product.couche2.label}
                </span>
              </div>
              {product.couche2.confirmedBy && (
                <div className="text-[10px] text-ink-faint">
                  Confirmé par {product.couche2.confirmedBy} le{' '}
                  {new Date(product.couche2.confirmedAt!).toLocaleDateString('fr-FR')}
                </div>
              )}
              <div className="flex items-center justify-between text-[11px] pt-0.5">
                <span className="text-ink-muted">Prêt pour S19 & Sage :</span>
                <span
                  className={cn(
                    'font-bold',
                    product.couche2.readyForS19 ? 'text-emerald-700' : 'text-amber-600',
                  )}
                >
                  {product.couche2.readyForS19 ? 'Prêt' : 'En attente'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Pillar 3: Tarifs d'Achat B2B & Sage */}
        <div className="p-4 space-y-2.5">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink-muted">
            <Building2 className="w-3.5 h-3.5 text-amber-600" />
            <span>3. Tarifs Grossistes B2B</span>
          </div>

          <div className="space-y-1.5 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-ink-muted">Prix Achat HT :</span>
              <span className="font-mono font-bold text-ink">
                {formatTnd(product.pricing.purchasePriceHt)}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-ink-muted">TVA Grossiste :</span>
              <span className="font-semibold text-ink">
                {product.pricing.purchaseVatRate !== null
                  ? `${product.pricing.purchaseVatRate} %`
                  : 'N/A'}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-ink-muted">Source Retenue :</span>
              <span className="font-semibold text-ink">
                {product.pricing.purchaseSourceKey ?? 'Aucune'}
              </span>
            </div>

            {/* Wholesaler offers list */}
            <div className="pt-1">
              <span className="text-[11px] text-ink-muted block mb-1">
                Offres disponibles ({product.pricing.offers.length}) :
              </span>
              {product.pricing.offers.length === 0 ? (
                <span className="text-[11px] text-ink-faint italic">Aucune offre grossiste liée</span>
              ) : (
                <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                  {product.pricing.offers.map((offer) => (
                    <div
                      key={offer.id}
                      className={cn(
                        'px-2 py-1 rounded text-[11px] space-y-0.5 transition-colors',
                        offer.isSelected
                          ? 'bg-amber-100/80 border border-amber-300 font-medium shadow-xs'
                          : 'bg-cream/60 border border-border/50',
                      )}
                    >
                      <div className="flex items-center justify-between gap-1.5">
                        <span className="truncate max-w-[8rem] font-semibold text-ink-soft" title={offer.sourceLabel}>
                          {offer.sourceLabel}
                        </span>
                        <div className="flex items-center gap-1 shrink-0">
                          {offer.promoLabel && (
                            <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[10px] font-bold bg-rose-100 text-rose-800 border border-rose-300">
                              {offer.promoLabel}
                            </span>
                          )}
                          <span className="font-mono font-bold text-ink">{formatTnd(offer.priceHt)}</span>
                        </div>
                      </div>
                      {offer.b2bProductName && (
                        <div className="text-[10px] text-ink-muted truncate font-sans flex items-center gap-1" title={`Désignation Grossiste : ${offer.b2bProductName}`}>
                          <Tag className="w-2.5 h-2.5 text-ink-ghost shrink-0" />
                          <span className="truncate">{offer.b2bProductName}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons Footer */}
      <div className="px-4 py-2.5 bg-cream/20 border-t border-border flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <a
            href={product.links.wooEditUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-muted hover:text-ink transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            <span>Éditer dans WooCommerce ↗</span>
          </a>
          <Button
            type="button"
            variant="outline"
            size="xs"
            disabled={isSyncing}
            onClick={() => onSyncFromWoo(product)}
            className="text-sky-700 hover:text-sky-800 hover:bg-sky-50 border-sky-300 font-semibold cursor-pointer"
            title="Rafraîchir immédiatement le statut, prix et stock depuis WooCommerce"
          >
            <RefreshCw className={cn("w-3 h-3", isSyncing && "animate-spin text-sky-600")} />
            <span>Actualiser depuis Woo</span>
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {product.operatorStatus === 'hide' || product.couche1.code === 'hide' ? (
            <>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold bg-zinc-200 text-zinc-800 border border-zinc-300 shadow-2xs">
                <EyeOff className="w-3.5 h-3.5 text-zinc-600" />
                Masqué du site
              </span>
              {product.stockQty !== -999 && (
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  className="text-amber-700 hover:text-amber-800 hover:bg-amber-50 border-amber-300 cursor-pointer shadow-2xs font-semibold gap-1"
                  disabled={isParkingStock}
                  onClick={() => onParkStock(product)}
                  title="Parquer le stock à -999 pour sortir du catalogue Facebook Ads"
                >
                  <PackageX className="w-3.5 h-3.5 text-amber-600" />
                  <span>{isParkingStock ? 'Parking en cours…' : 'Parquer stock (-999)'}</span>
                </Button>
              )}
            </>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="xs"
              className="text-rose-700 hover:text-rose-800 hover:bg-rose-50 border-rose-300 cursor-pointer shadow-2xs font-semibold"
              disabled={isHiding}
              onClick={() => onHide(product)}
              title="Produit discontinué : masquer du catalogue et retirer du site web WooCommerce"
            >
              {isHiding ? (
                <div className="w-3.5 h-3.5 border-2 border-rose-600 border-t-transparent rounded-full animate-spin" />
              ) : (
                <EyeOff className="w-3.5 h-3.5 text-rose-600" />
              )}
              <span>Masquer du site</span>
            </Button>
          )}

          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={() => onNavigate('/hidden-products')}
            title="Consulter la console Produits masqués"
          >
            <span>Produits masqués</span>
            <ArrowRight className="w-3 h-3" />
          </Button>

          {product.couche1.code !== 'approved' && (
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => onNavigate(product.links.workbenchUrl)}
            >
              <span>Couche 1 (Workbench)</span>
              <ArrowRight className="w-3 h-3" />
            </Button>
          )}

          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={() => onNavigate(product.links.finalEanUrl)}
          >
            <span>Couche 2 (Final Match)</span>
            <ArrowRight className="w-3 h-3" />
          </Button>

          <Button
            type="button"
            variant="soft"
            size="xs"
            onClick={() => onNavigate(product.links.b2bUrl)}
          >
            <span>Prix B2B</span>
            <ArrowRight className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}
